import { isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";


/**
 * SW2-2345 — the one definition of a map's version list.
 *
 * This was the body of a useMemo inside MapEditor/Header.tsx, and the print modal had a
 * copy of it. The copy was written two days before SW2-2347 rewrote the original and had
 * already drifted: on map 14991 it produced 210 entries against the editor's 203, ordered
 * by inspection rather than by date, with none of the labels or the sole-handle
 * numbering. A user picking "This Version's Log Only" was choosing from a list that did
 * not match the one they had just been looking at.
 *
 * Two implementations of "which versions does this map have" is the failure this ticket
 * and SW2-2347 both spent their review rounds on, so there is one, here, and both callers
 * import it.
 */

/**
 * The same day `dateFormat` in `utils` names, spelled in date-fns tokens rather than
 * moment's. Not imported from there: that constant is 'MM-DD-YYYY', which date-fns reads
 * as month, day-of-YEAR, era — a different date. It stays local until the shared one moves.
 */
const US_DAY = "MM-dd-yyyy";

/** UTC because these are calendar days, not instants: the day must not move with the viewer's zone. */
const utcDay = (date: any, pattern = "yyyy-MM-dd") => {
  const parsed = new Date(date);

  return isValid(parsed) ? formatInTimeZone(parsed, "UTC", pattern) : null;
};

export type MapVersion = {
  id?: number;
  inspection_id?: number;
  inspection_number: number;
  versions: string[];
  created_date?: string;
  /**
   * SW2-2345 — day -> the forms whose print would draw the map set this day came from.
   *
   * Resolved server-side the way `findSiteMapsDocType` resolves it, fallback included,
   * so a form with no site map set of its own still lists the set it actually falls back
   * to. Not the same as "the form that owns this map": a project's primary form usually
   * owns no set at all.
   */
  version_form_types?: Record<string, number[]>;
  /** The form this inspection is on. Carries the scope when it has no version days. */
  inspection_template?: { inspection_type_id?: number | null } & Record<string, any>;
};

export type VersionOption = {
  /** `<inspectionId>|<yyyy-MM-dd>` — the inspection still selects which findings render. */
  value: string;
  label: string;
  inspectionId?: number;
  isInspectionDay: boolean;
};

/**
 * SW2-2345 — the versions belonging to one inspection form.
 *
 * Two forms on a project have their own maps and so their own versions, and picking a
 * day from the other form's maps prints that form's maps instead — the map set is
 * resolved from the form, not from the day. A day with no form types recorded came from
 * the project's unscoped map set and is offered under every form.
 *
 * With no form selected nothing is narrowed, which is the single-form case and every
 * caller outside the project print modal.
 */
export const scopeVersionsToFormType = (
  mapVersions: MapVersion[],
  formTypeId?: number | string | null,
): MapVersion[] => {
  const typeId = Number(formTypeId);

  if (!formTypeId || Number.isNaN(typeId)) return mapVersions;

  return mapVersions
    .map((entry) => ({
      ...entry,
      versions: entry.versions.filter((day) => {
        // No tagging at all means a server that does not send it; narrowing on absent
        // data would hide every version rather than fail visibly.
        if (!entry.version_form_types) return true;

        const types = entry.version_form_types[day];

        // A day the server tagged with no form is one no form's print resolves to —
        // choosing it would draw a different map set than the day describes. A day it
        // did not mention at all should not happen, and is kept rather than silently
        // dropped, because losing a real version is the worse of the two failures.
        return types ? types.includes(typeId) : true;
      }),
    }))
    .filter((entry, index) => {
      if (entry.versions.length > 0) return true;

      /**
       * No day survived, so the entry has to justify itself another way.
       *
       * An inspection that stamped nothing still gets a picker entry, dated by its own
       * created_date (SW2-2346) — but only under its own form. Without this, every one
       * of the 4,696 inspections in multi-form projects that stamped no map content
       * appeared under every form, and choosing one printed its own form's maps rather
       * than the one selected. An inspection whose days were all filtered out belongs to
       * another form and is gone by the same test.
       */
      if (mapVersions[index].versions.length > 0) return false;

      return entry.inspection_template?.inspection_type_id === typeId;
    });
};

/** The day half of an option value, or "" for the dateless fallback entry. */
export const versionDayOf = (value?: string) => (value ?? "").split("|")[1] || "";

export type BuildVersionOptionsOpts = {
  /**
   * SW2-2345 — keep an inspection that pass one would erase entirely.
   *
   * True for the map EDITOR, where an inspection you cannot select is an inspection you
   * cannot draw against and whose findings you cannot see. False for the PRINT modal,
   * where it buys nothing and costs a duplicate: the site map report renders no findings
   * (`includeFindings: false` in siteMapReportDirector), so the only thing the inspection
   * half of a selection could change is what the print reads — and on a shared day it
   * reads the same thing. Measured on project 3252, 08-05-2024: inspections 198375 and
   * 199570 return identical item sets (5 and 13 on the two maps) and identical changelogs
   * (22 entries scoped, 156 unscoped). Two rows, one PDF.
   */
  keepUnreachableInspections?: boolean;
};

export const buildVersionOptions = (
  mapVersions: MapVersion[],
  { keepUnreachableInspections = true }: BuildVersionOptionsOpts = {},
): VersionOption[] => {
  const inspections = [...mapVersions].sort(
    (a, b) => b.inspection_number - a.inspection_number,
  );
  const hasNoInspections =
    inspections.length === 1 && inspections[0].id === -1;

  const ordered = inspections.flatMap((inspection) => {
      const inspectionId = inspection.id;
      const firstMinor = inspection.inspection_number === 0 ? 1 : 0;
      const versions = [...inspection.versions].sort();

      /**
       * SW2-2347 — say which days are inspections.
       *
       * Every entry used to read alike, so a day the inspector was on site looked
       * exactly like a day someone adjusted a legend item from a desk. The number
       * hints at it — `.0` is usually the inspection — but not reliably: an
       * inspection that edited the map on some other day has no `.0` at all.
       *
       * The date is what distinguishes them. A version day equal to the inspection's
       * own date IS that inspection; anything else is a map change made between
       * inspections. A native select renders text only, so the distinction has to be
       * in the label.
       */
      const inspectionDay = inspection?.created_date
        ? utcDay(inspection.created_date)
        : null;

      const describeDay = (date?: any) => {
        const day = date ? utcDay(date) : null;

        // An edit made ON the inspection's own day is still that inspection's day —
        // the inspection is the thing worth naming.
        // "Insp." rather than the inspection's type: the type adds width to every
        // row in a list that can run past a hundred entries, and the question this
        // label answers is which days are inspections and which are map changes
        // between them. The type is a click away on the inspection itself.
        return day && day === inspectionDay ? " · Insp." : " · Map edit";
      };

      const getLabel = (index: any, date?: any) => {
        /**
         * SW2-2345 — a row with no readable date is named without one.
         *
         * `utcDay` returns null for a missing or unparseable value, and interpolating
         * that produced "#5.0 on null" in the picker — reachable on the dateless
         * fallback entry for an inspection whose created_date is absent. A row that
         * simply reads "#5.0" is honest; one that reads "on null" looks broken.
         */
        const day = utcDay(date, US_DAY);
        const on = day ? ` on ${day}` : "";

        return (
          (hasNoInspections
            ? `v${inspection.inspection_number + index + firstMinor}${on}`
            : `#${inspection.inspection_number}.${index + firstMinor}${on}`) +
          // A map with no inspections at all has nothing to contrast against, so the
          // suffix would be noise on every row.
          (hasNoInspections ? "" : describeDay(date))
        );
      };

      if (versions.length === 0) {
        /**
         * SW2-2346 — an inspection that stamped no positionables still gets an entry,
         * labelled with its own created_date. That date has to travel in the value
         * too: the value is what applyMapVersion splits, so an empty one left
         * selectedVersionDate null and every write from that entry omitted
         * versionDate and stamped today — the exact bug this ticket fixes, still
         * live on the one picker entry that is not derived from a positionable.
         *
         * Reachable in real data: an inspection owns no map edits whenever its own
         * were orphaned by a deleted inspection (SW2-2347, measured at 305 maps), and
         * on any inspection nobody has drawn on yet.
         *
         * Normalised to yyyy-MM-dd because the server now rejects anything else with
         * a 400 rather than quietly reading it as today. Left empty when the date is
         * missing or unparseable, which restores the previous behaviour for that
         * entry rather than sending something the server will refuse.
         */
        const fallbackVersionDate = inspection?.created_date
          ? utcDay(inspection.created_date) ?? ""
          : "";

        return [
          {
            value: `${inspectionId}|${fallbackVersionDate}`,
            label: getLabel(0, inspection?.created_date),
            inspectionId,
            // The fallback IS the inspection's own day, by construction.
            isInspectionDay: true,
          },
        ];
      }

      return versions
        .map((date, index) => ({
          value: `${inspectionId}|${date}`,
          label: getLabel(index, date),
          inspectionId,
          isInspectionDay:
            Boolean(inspectionDay) &&
            utcDay(date) === inspectionDay,
        }))
        .reverse();
    })
    /**
     * SW2-2347 — one timeline, newest first.
     *
     * The list was grouped by inspection and only ordered within each group, so the
     * no-inspection bucket (inspection_number 0) always sank to the bottom whatever
     * its dates. That was tolerable while the bucket was nearly empty; this ticket
     * recovers days into it that were previously unreachable, and they can fall
     * anywhere in the timeline — York Street ended up showing 04-02-2026 below
     * 05-12-2023.
     *
     * Not cosmetic. `versionOptions[0]` is what decides `isLatestMapVersion`, and so
     * where editing is permitted: with the old order the editor called a 2023 day the
     * latest version while a real 2026 one sat at the bottom, unrecognised.
     */
    .sort((a, b) => {
      const dayOf = (value: string) => value.split("|")[1] || "";

      // An entry with no date is a fallback for an inspection that owns no version.
      // Keep those last rather than letting an empty string sort them to the top.
      return dayOf(b.value).localeCompare(dayOf(a.value));
    });

  /**
   * SW2-2347 — number a map edit against the inspection it FOLLOWS, by date.
   *
   * `#N.M` reads as "the M-th map state after inspection N", so the major number has
   * to come from position in the timeline, not from whichever inspection happens to
   * be stamped on the row.
   *
   * Two ways that went wrong. A day whose inspection was deleted had no N at all and
   * fell back to `#0.n`, so the list ran 99, 98, 0.1, 97. And a back-dated edit kept
   * its stamped inspection's number wherever it landed: project 4460 showed `#74.0`
   * sitting between `#5.0` and `#4.0`, on the same date as `#4.0`, because inspection
   * 74 had filed an edit effective 09-30-2024.
   *
   * Numbering it against the preceding inspection puts it back in sequence and says
   * something true: "the first map change after inspection 97". Attribution by
   * proximity is fine for a LABEL, which is why the value is untouched — it still
   * carries -1, so the read still treats the day as belonging to no inspection. This
   * is presentation only.
   *
   * Sub-indices continue after that inspection's own, so an inspection with #97.0 and
   * #97.1 of its own gives the next orphan day #97.2 rather than a duplicate.
   */
  /**
   * SW2-2347 — one entry per day, because a version IS a day.
   *
   * SW2-2346 settled that a version is a (map, effective date) pair, but derivation
   * groups by (inspection, date), so one day could produce several entries. Project
   * 4460 showed "#4.1 on 09-30-2024 · Map edit" directly above "#4.0 on 09-30-2024 ·
   * Insp." — two rows for one day, showing the same map, because an edit stamped to
   * another inspection happened to fall on an inspection's date. 3,430 days estate-
   * wide produce more than one entry and 2,928 of those are this shape.
   *
   * They render identically: the date predicate decides which items appear, and both
   * entries carry the same date. So the duplicate is noise.
   *
   * The exception is two INSPECTIONS on one day. Those are kept, because the
   * inspection is what selects which findings you see — the drawn map is the same,
   * the findings are not, and collapsing them would make one inspection's findings
   * unreachable.
   *
   * "Is this an inspection day" is not enough to express that exception, because an
   * inspection need not own a version on its own date — it may only ever have edited
   * the map on some other day (the "no `.0`" shape above, 6,706 inspections estate-
   * wide). If that day is also some other inspection's own date, every one of its
   * entries is a duplicate by this test and the inspection leaves the picker
   * altogether: 655 inspections across 559 maps. Its findings then cannot be selected
   * from the map editor at all, and opening the editor from that inspection lands
   * silently on a different one — the same unreachability this ticket exists to fix,
   * reintroduced from the other end. So the last entry standing for an inspection is
   * kept whatever day it falls on; `ordered` is newest-first, so that is its newest.
   */
  const dayOf = (option: VersionOption) => option.value.split("|")[1] || "";

  const seenInspectionDays = new Set<string>();

  ordered.forEach((option) => {
    if (option.isInspectionDay) seenInspectionDays.add(dayOf(option));
  });

  /**
   * Pass one: one entry per day. An inspection day always survives — that is the
   * two-inspections-on-one-day exception, and it is why the test below asks whether
   * the day is spoken for rather than simply counting entries.
   *
   * `keptDays` is the part that cannot be inferred from `seenInspectionDays`: two map
   * edits on a day that is nobody's inspection date are both non-inspection entries,
   * so neither is in `seenInspectionDays` and without this both would survive. That
   * pair is reachable by construction — the -1 bucket's days are exactly the days no
   * live inspection owns — and it is worse than untidy on the newest day, where
   * `isLatestMapVersion` matches one entry of the pair and a save from the other
   * back-dates itself.
   */
  const keptDays = new Set<string>();
  const kept = new Set<VersionOption>();

  ordered.forEach((option) => {
    const day = dayOf(option);

    if (option.isInspectionDay) {
      keptDays.add(day);
      kept.add(option);

      return;
    }

    if (seenInspectionDays.has(day) || keptDays.has(day)) return;

    keptDays.add(day);
    kept.add(option);
  });

  /**
   * Pass two: an inspection that lost EVERY entry to pass one keeps its newest.
   *
   * Deciding this after the fact rather than during the filter is the whole point. A
   * greedy rescue — keep the newest entry of any inspection without a day of its own —
   * fires for inspections that were never at risk, because one of their other days
   * survives on its own. On project 4460 that puts "#4.1 · Map edit" straight back
   * above "#4.0 · Insp." on 09-30-2024: the duplicate this dedupe exists to remove.
   *
   * -1 is the no-inspection bucket, not an inspection: it selects no findings, so it
   * has nothing to be made unreachable. An entry with no id is in the same position.
   */
  const representedInspections = new Set<number>();
  const soleHandleFor = new Set<VersionOption>();

  kept.forEach((option) => {
    if (option.inspectionId != null) representedInspections.add(option.inspectionId);
  });

  ordered.forEach((option) => {
    if (!keepUnreachableInspections) return;

    const { inspectionId } = option;

    if (inspectionId == null || inspectionId === -1) return;
    if (representedInspections.has(inspectionId)) return;

    // `ordered` is newest-first, so the first one reached is the newest.
    representedInspections.add(inspectionId);
    soleHandleFor.add(option);
    kept.add(option);
  });

  const timeline = ordered.filter((option) => kept.has(option));

  const oldestFirst = [...timeline].reverse();
  const usedMinors = new Map<number, number>();

  // An inspection day keeps its own number; those are the fixed points everything
  // else is numbered against.
  timeline.forEach((option) => {
    if (!option.isInspectionDay) return;

    const match = /^#(\d+)\.(\d+)/.exec(option.label);

    if (!match) return;

    const majorNumber = Number(match[1]);

    if (majorNumber === 0) return;

    usedMinors.set(
      majorNumber,
      Math.max(usedMinors.get(majorNumber) ?? 0, Number(match[2])),
    );
  });

  let precedingMajor: number | null = null;

  oldestFirst.forEach((option) => {
    if (option.isInspectionDay) {
      const match = /^#(\d+)\./.exec(option.label);
      const majorNumber = match ? Number(match[1]) : null;

      if (majorNumber !== null && majorNumber > 0) precedingMajor = majorNumber;

      return;
    }

    /**
     * An entry kept ONLY because it is its inspection's last handle keeps that
     * inspection's own number, and loses the minor.
     *
     * Numbering it against the inspection it follows is right for an ordinary map
     * edit, and exactly wrong here: this row is the one and only way to reach
     * inspection N and its findings, so a label naming some other inspection makes it
     * unfindable. On Anthology North page 4, inspection #190's sole entry rendered as
     * "#151.1 on 08-20-2024 · Map edit" and #190 appeared nowhere in a 203-entry list.
     *
     * The bare "#190" — no minor — says what it is: inspection 190, reached by the
     * map edit it filed rather than by a day of its own. It also explains the second
     * entry on that day, which is inspection #151's own.
     */
    if (soleHandleFor.has(option)) {
      option.label = option.label.replace(/^#(\d+)\.\d+/, "#$1");

      return;
    }

    // A map edit before the first inspection has nothing to hang on, so it keeps
    // whatever it had rather than inventing an inspection for it.
    if (precedingMajor === null) return;

    const nextMinor = (usedMinors.get(precedingMajor) ?? 0) + 1;

    usedMinors.set(precedingMajor, nextMinor);
    option.label = option.label.replace(/^#\d+\.\d+/, `#${precedingMajor}.${nextMinor}`);
  });

  return timeline;
};
